<template>
  <Teleport to="body">
    <Transition name="tooltip">
      <div
        v-if="visible && event"
        ref="tooltipRef"
        class="event-tooltip"
        :style="tooltipStyle"
      >
        <!-- 头部色条 -->
        <div class="tooltip-header" :style="{ backgroundColor: typeColor }"></div>

        <!-- 内容区 -->
        <div class="tooltip-content">
          <!-- 类型标签和时间 -->
          <div class="tooltip-meta">
            <span class="tooltip-type" :style="{ backgroundColor: typeColor, color: '#fff' }">
              {{ typeLabel }}
            </span>
            <span class="tooltip-time">{{ formattedTime }}</span>
          </div>

          <!-- 标题 -->
          <h4 class="tooltip-title">{{ event.title }}</h4>

          <!-- 描述 -->
          <p v-if="event.description" class="tooltip-description">
            {{ event.description }}
          </p>

          <!-- 详细信息 -->
          <div class="tooltip-details">
            <!-- 地点 -->
            <div v-if="event.location" class="tooltip-detail">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span>{{ event.location }}</span>
            </div>

            <!-- 参与人 -->
            <div v-if="event.attendees" class="tooltip-detail">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
              <span>{{ event.attendees }}</span>
            </div>

            <!-- CRM关联 -->
            <div v-if="event.links?.length" class="tooltip-detail">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              <div class="tooltip-links">
                <span
                  v-for="link in event.links"
                  :key="link.type"
                  class="tooltip-link-tag"
                >
                  {{ linkLabel(link.type) }}
                </span>
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="tooltip-actions">
            <button class="tooltip-btn tooltip-btn--primary" @click="handleEdit">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              编辑
            </button>
            <button class="tooltip-btn" @click="handleComplete" v-if="event.type === 'task'">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              完成
            </button>
            <button class="tooltip-btn tooltip-btn--danger" @click="handleDelete">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              删除
            </button>
          </div>
        </div>

        <!-- 箭头 -->
        <div class="tooltip-arrow" :style="{ borderColor: `transparent transparent ${typeColor} transparent` }"></div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue'

export default {
  name: 'EventTooltip',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: null
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    placement: {
      type: String,
      default: 'top'
    }
  },
  emits: ['edit', 'complete', 'delete', 'close'],
  setup(props, { emit }) {
    const tooltipRef = ref(null)

    // 类型颜色映射
    const typeColors = {
      meeting: '#3B82F6',
      call: '#10B981',
      task: '#F59E0B',
      deadline: '#EF4444',
      other: '#6B7280'
    }

    // 类型标签映射
    const typeLabels = {
      meeting: '会议',
      call: '电话',
      task: '任务',
      deadline: '截止',
      other: '其他'
    }

    // 计算属性
    const typeColor = computed(() => props.event?.color || typeColors[props.event?.type] || typeColors.other)
    const typeLabel = computed(() => typeLabels[props.event?.type] || '日程')

    const formattedTime = computed(() => {
      if (!props.event) return ''
      const { time, duration } = props.event
      if (!time) return ''

      const [hours, minutes] = time.split(':').map(Number)
      const endMinutes = hours * 60 + minutes + (duration || 0)
      const endHours = Math.floor(endMinutes / 60)
      const endMins = endMinutes % 60

      const formatTime = (h, m) => {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      }

      return duration ? `${formatTime(hours, minutes)} - ${formatTime(endHours, endMins)}` : time
    })

    // 菜单位置
    const tooltipStyle = computed(() => {
      const { x, y, placement } = props
      const tooltip = tooltipRef.value
      const offset = 12 // 偏移量

      let left = x
      let top = y

      if (tooltip) {
        const rect = tooltip.getBoundingClientRect()

        switch (placement) {
          case 'top': {
            left = x - rect.width / 2
            top = y - rect.height - offset
            break
          }
          case 'bottom': {
            left = x - rect.width / 2
            top = y + offset
            break
          }
          case 'left': {
            left = x - rect.width - offset
            top = y - rect.height / 2
            break
          }
          case 'right': {
            left = x + offset
            top = y - rect.height / 2
            break
          }
        }

        // 防止超出屏幕
        left = Math.max(10, Math.min(left, window.innerWidth - rect.width - 10))
        top = Math.max(10, Math.min(top, window.innerHeight - rect.height - 10))
      }

      return {
        left: `${left}px`,
        top: `${top}px`
      }
    })

    // 链接类型标签
    function linkLabel(type) {
      const labels = {
        customer: '客户',
        opportunity: '商机',
        contract: '合同',
        project: '项目'
      }
      return labels[type] || type
    }

    // 处理操作
    function handleEdit() {
      emit('edit', props.event)
    }

    function handleComplete() {
      emit('complete', props.event)
    }

    function handleDelete() {
      emit('delete', props.event)
    }

    // 点击外部关闭
    function handleClickOutside(e) {
      if (props.visible && tooltipRef.value && !tooltipRef.value.contains(e.target)) {
        emit('close')
      }
    }

    watch(() => props.visible, (visible) => {
      if (visible) {
        document.addEventListener('click', handleClickOutside)
      } else {
        document.removeEventListener('click', handleClickOutside)
      }
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      tooltipRef,
      typeColor,
      typeLabel,
      formattedTime,
      tooltipStyle,
      linkLabel,
      handleEdit,
      handleComplete,
      handleDelete
    }
  }
}
</script>

<style scoped>
.event-tooltip {
  position: fixed;
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 10000;
  pointer-events: auto;
}

.tooltip-header {
  height: 4px;
}

.tooltip-content {
  padding: 16px;
}

.tooltip-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tooltip-type {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.tooltip-time {
  font-size: 12px;
  color: #64748b;
}

.tooltip-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.tooltip-description {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.tooltip-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.tooltip-detail {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.tooltip-detail svg {
  flex-shrink: 0;
  margin-top: 1px;
  color: #94a3b8;
}

.tooltip-links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tooltip-link-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background: #f1f5f9;
  border-radius: 4px;
  font-size: 11px;
  color: #475569;
}

.tooltip-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.tooltip-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.tooltip-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.tooltip-btn--primary {
  color: white;
  background: #3b82f6;
  border-color: #3b82f6;
}

.tooltip-btn--primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.tooltip-btn--danger {
  color: #ef4444;
  background: #fef2f2;
  border-color: #fecaca;
}

.tooltip-btn--danger:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.tooltip-arrow {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-width: 8px;
  border-style: solid;
}

/* 过渡动画 */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.2s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
